import { useEnv } from '@sparklayer-io/spark-dash-core/context/env-context';
import { trackUpgradeClicked } from '@sparklayer-io/spark-dash-core/lib/mixpanel';
import {
  PopularAddons,
  type PopularAddonsItem,
} from '@sparklayer-io/spark-dash-pages/components/popular-addons';
import { useHandlers, useSite } from '@sparklayer-io/spark-dash-pages/context/page-context';
import { upgradeQuoting, upgradeSalesAgents } from '@sparklayer-io/spark-dash-ui/assets';
import { Button } from '@ui/themed';
import { t } from 'i18next';
import upgrade2Img from '../../../assets/upgrade-2.png';
import upgrade3Img from '../../../assets/upgrade-3.png';
import upgrade5Img from '../../../assets/upgrade-5.png';
import type { I18nPrefix } from '../../../i18n/prefix';

const COMPONENT = 'features:home.components.upsell_plan' satisfies I18nPrefix;

export function UpsellPlan() {
  const env = useEnv();
  const site = useSite();
  const { navigationHandler } = useHandlers();
  const plan = env.billing.plan_key;

  const cta = (needsUpgrade: boolean, title: string, action: string) =>
    needsUpgrade ? (
      <Button
        variant="secondary"
        icon="upgrade"
        onClick={() => {
          trackUpgradeClicked(site, title);
          navigationHandler.navigateById('plan');
        }}
      >
        {t('buttons:upgrade')}
      </Button>
    ) : (
      <Button
        variant="secondary"
        icon="settings"
        onClick={() => navigationHandler.navigate(action)}
      >
        {t('buttons:enable')}
      </Button>
    );

  const items: PopularAddonsItem[] = [
    {
      title: t(`${COMPONENT}.sales_agents.title`),
      image: upgradeSalesAgents,
      description: t(`${COMPONENT}.sales_agents.description`),
      link: 'https://docs.sparklayer.io/sales-agent-ordering',
      plan: 'v2_basic',
      cta: cta(plan === 'basic_free', t(`${COMPONENT}.sales_agents.title`), '/sales-agents'),
    },
    {
      title: t(`${COMPONENT}.invoices.title`),
      image: upgrade5Img,
      description: t(`${COMPONENT}.invoices.description`),
      link: 'https://docs.sparklayer.io/invoices',
      plan: 'v2_growth',
      cta: cta(plan === 'v2_basic', t(`${COMPONENT}.invoices.title`), '/configuration/general'),
    },
    {
      title: t(`${COMPONENT}.quoting.title`),
      image: upgradeQuoting,
      description: t(`${COMPONENT}.quoting.description`),
      link: 'https://docs.sparklayer.io/quoting',
      plan: 'v2_pro',
      cta: cta(plan === 'v2_basic', t(`${COMPONENT}.quoting.title`), '/configuration/general'),
    },
    {
      title: t(`${COMPONENT}.company_users.title`),
      image: upgrade2Img,
      description: t(`${COMPONENT}.company_users.description`),
      link: 'https://docs.sparklayer.io/company-users',
      plan: 'v2_pro',
      cta: cta(
        plan === 'v2_basic' || plan === 'v2_growth',
        t(`${COMPONENT}.company_users.title`),
        '/configuration/general',
      ),
      className: 'hidden 2xl:block',
    },
    {
      title: t(`${COMPONENT}.api_access.title`),
      image: upgrade3Img,
      description: t(`${COMPONENT}.api_access.description`),
      link: 'https://docs.sparklayer.io/sparklayer-api',
      plan: 'v2_pro',
      cta: cta(
        plan === 'v2_basic' || plan === 'v2_growth',
        t(`${COMPONENT}.api_access.title`),
        '/configuration/api',
      ),
    },
  ];

  return <PopularAddons items={items} />;
}
